import React, { useEffect } from 'react';
import { useState } from 'react';
import FormControlError from '../FormControlError';
import identity from 'lodash/identity';
import uniqueId from 'lodash/uniqueId';

import './CheckboxField.less';

/**
 * @typedef TCheckboxOption
 * @type {{
 *  value?:string;
 *  title?:string;
 *  checked?:boolean;
 *  disabled?:boolean;
 *  label?:React.ReactNode;
 *  labelClassName?:string;
 *  ariaLabel?:string;
 *  postOptionSlot?:React.ReactNode;
 * }}
 *
 * @typedef TPropsCheckboxField
 * @type {{
 *  ariaLabel?: string;
 *  ariaDescribedBy?: string;
 *  id?: string;
 *  title?: string;
 *  name?: string;
 *  required?: boolean;
 *  disabled?: boolean;
 *  checked?: boolean;
 *  className?: string;
 *  value?: string;
 *  label?: React.ReactNode;
 *  onChange?(name:string,state:boolean,value?:string,option?:TCheckboxOption):void;
 *  horizontal?:boolean;
 *  classNameWrapper?:string;
 *  customErrorMessage?:React.ReactNode;
 *  customErrorPrefix?: string|null;
 *  customValidator?(params:{isChecked:Record<string, boolean>, errorMessage:React.ReactNode}):React.ReactNode;
 *  options?:TCheckboxOption[];
 *  postControlSlot?:React.ReactNode;
 * }}
 * @param {TPropsCheckboxField} props
 * @returns {React.ReactElement}
 */
export const CheckboxField = (props) => {
  const {
    title,
    checked,
    required,
    disabled,
    customErrorPrefix,
    name,
    label,
    horizontal,
    customValidator,
    onChange,
    ariaLabel,
    ariaDescribedBy,
    classNameWrapper,
    customErrorMessage,
    value,
    options,
    postControlSlot,
  } = props;
  const [isDirty, setDirty] = useState(false);
  const [checkedMap, setCheckedMap] = useState(/** @type {Record<string, boolean>} */({}));
  const [errorMessage, setErrorMessage] = useState((/** @type {React.ReactNode} */(null)));
  const values = options ?? [{ title, label: label ?? title, value, checked, disabled, ariaLabel, }];
  const classNames = [
    'lsn-control-checkboxes',
    errorMessage ? 'has-error' : '',
    horizontal ? 'horizontal' : '',
    classNameWrapper
  ].filter(identity).join(' ');
  const uiLibClassNames = ['form-checkbox'];
  const classNameControl = [
    ...uiLibClassNames,
    'lsn-control lsn-control-checkbox',
    errorMessage ? 'has-error' : ''
  ].filter(identity).join(' ');
  const isGroup = options && options.length > 0;
  const [controlUID] = useState(props.id || uniqueId(`lsn_ctrl_`));

  /**
   *
   * @param {Record<string, boolean>} checkStates
   */
  const isAnyChecked = (checkStates) => Object.values(checkStates).some(identity);

  /**
   *
   * @param {Record<string, boolean>} checkStates
   */
  const _getNativeErrorMessage = (checkStates) => {
    const anyChecked = isAnyChecked(checkStates);
    if (required ? anyChecked : true) {
      return '';
    }
    const errorMessage = `${customErrorPrefix ?? title ?? 'Entry'} is required`;
    const result = customValidator ? customValidator({ isChecked: checkStates, errorMessage }) ?? '' : errorMessage;
    return result ?? '';
  }

  /**
   *
   * @param {Record<string, boolean>} checkStates
   */
  const refreshErrorMessage = (checkStates) => {
    const isManualErrControl = 'customErrorMessage' in props;
    const errorMessage = disabled ? '' : isManualErrControl ? customErrorMessage ?? '' : isDirty ? _getNativeErrorMessage(checkStates) : '';
    setErrorMessage(errorMessage);
  };

  /**
   *
   * @param {React.FocusEvent<HTMLInputElement>} e
   */
  const handleFocus = (e) => {
    //reset validations onFocus
    setDirty(true);
  }

  /**
   *
   * @param {React.FocusEvent<HTMLInputElement>} e
   */
  const handleBlur = (e) => {
    setDirty(true);
  }

  /**
   *
   * @param {boolean} checkedState
   * @param {TCheckboxOption} option
   */
  const handleChange = (checkedState, option) => {
    if (option.disabled) { return; }
    setDirty(true);
    setCheckedMap({ ...checkedMap, [option.value ?? '']: checkedState, });
    onChange?.(name ?? '', checkedState, option.value, option);
  }

  // update the isChecked state when the checked prop is being changed
  useEffect(() => {
    setCheckedMap({ ...checkedMap, [value ?? '']: !!checked, });
    setDirty(typeof checked === 'boolean' || isDirty);
  }, [checked]);

  // update the isChecked state when the options prop is being changed
  useEffect(() => {
    options ?
      setCheckedMap((options ?? [])?.reduce((r, o) => ({ ...r, [o.value ?? '']: !!o.checked }), checkedMap)) : void 0;
  }, [options]);

  // update the vaidity when control gets dirty or the value is being changed
  useEffect(() => {
    refreshErrorMessage(checkedMap);
  }, [isDirty, checkedMap, customErrorMessage]);

  return (
    <>
      {/* the form-checkbox className must be there. (https://picturepan2.github.io/spectre/elements/forms.html) */}
      <div
        {
        ...isGroup ? {
          role: 'group',
          'aria-label': ariaLabel,
          'aria-describedby': ariaDescribedBy,
          'aria-errormessage': `${controlUID}_error_msg`,
          'aria-invalid': !!errorMessage,
        } : {}
        }

        data-value={Object.entries(checkedMap).map(e => `${e[0]}:${e[1]}`).join(';')}
        className={classNames}>
        {/* for consistency with the radio control */}
        {values.map((v, idx) => (
          <label
            className={classNameControl}
            key={`_lsn_ctrl_checkbox_key_${idx}`}
            onClick={(e) => {
              // prevent the click event from being propagated to the input element
              e.preventDefault();
              return false;
            }}>
            <input
              aria-hidden={true}

              id={isGroup ? `${controlUID}_${idx}` : controlUID}
              type="checkbox"
              tabIndex={-1}
              name={name}
              value={v.value}
              checked={checkedMap[v.value ?? ''] ?? false}
              disabled={v.disabled}
              required={required && !isAnyChecked(checkedMap)}
              title={title}

              onBlur={handleBlur}
              onFocus={handleFocus}
              onInvalid={handleBlur}
              onChange={(e) => handleChange(e.target.checked, v)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === 'Space') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleChange(!checkedMap[v.value ?? ''], v);
                }
              }}
            />
            {/* the form-icon className must be there. (https://picturepan2.github.io/spectre/elements/forms.html) */}
            <i
              role={'checkbox'}
              aria-label={v.ariaLabel ?? void 0}
              aria-labelledby={v.ariaLabel ? void 0 : `${controlUID}_${idx}_label`}
              aria-checked={checkedMap[v.value ?? ''] ?? false}
              aria-required={required && !isAnyChecked(checkedMap)}

              {
              ...isGroup ? {} : {
                'aria-errormessage': `${controlUID}_error_msg`,
              }
              }

              tabIndex={v.disabled ? -1 : 0}
              onBlur={handleBlur}
              onFocus={handleFocus}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.code === 'Space') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleChange(!checkedMap[v.value ?? ''], v);
                }
              }}
              onClick={() => handleChange(!checkedMap[v.value ?? ''], v)}
              className={'form-icon'} />
            <span className={['suffix', v.labelClassName].filter(Boolean).join(' ')}
              id={`${controlUID}_${idx}_label`}
              onClick={() => handleChange(!checkedMap[v.value ?? ''], v)}>{v.label}</span>
            {
              v.postOptionSlot
            }
          </label>
        ))}

      </div>
      {
        required ? <div className='control-error-wrapper'>
          <FormControlError id={`${controlUID}_error_msg`} show={!!errorMessage}>{errorMessage}</FormControlError>
        </div> : null
      }
      {postControlSlot}
    </>
  );
}

export default CheckboxField;
